export type AgentDateTimeOptions = {
  /** Test or explicit display override. Omit to use the browser's current timezone. */
  timeZone?: string;
  locale?: string;
};

/**
 * Formats an API timestamp in the browser's local timezone.
 *
 * Offset-bearing ISO timestamps (including Python's six-digit fractional seconds)
 * represent an instant. Offset-free timestamps retain JavaScript's local-time
 * semantics; this helper never silently appends UTC. Missing, invalid, or
 * unformattable values return null so callers can omit the timestamp or render a
 * friendly fallback without exposing a raw API value.
 */
export const formatAgentDateTime = (
  value: string | null | undefined,
  options: AgentDateTimeOptions = {},
): string | null => {
  if (typeof value !== 'string' || value.trim() === '') return null;

  const date = new Date(value);
  if (Number.isNaN(date.valueOf())) return null;

  try {
    return new Intl.DateTimeFormat(options.locale ?? 'en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      timeZoneName: 'short',
      ...(options.timeZone ? { timeZone: options.timeZone } : {}),
    }).format(date);
  } catch {
    return null;
  }
};
